import { z } from "zod";
import { Statuses, type Status } from "./core";
import { humanInterruptConfigSchema } from "./interrupt";

// Artifacts: durable, addressable things the agent surfaces to the user, held in
// the `artifacts` state channel and rendered by `type`. This file is the single
// source of the vocabulary + shapes — the tool (args), the reducer (TS type),
// and the frontend (renderer props) all reference it; none redefine it.

/** Broad channel catalog. The `artifacts` channel holds any of these. */
export const ArtifactTypes = ["keywords", "table", "chart", "website", "ads", "plan"] as const;
export type ArtifactType = (typeof ArtifactTypes)[number];

/** DisplayArtifact: the content-backed subset the agent surfaces via the tool. */
export const DisplayArtifactTypes = ["keywords"] as const;
export type DisplayArtifactType = (typeof DisplayArtifactTypes)[number];

/**
 * Supersedable types hold at most ONE active artifact at a time. Surfacing a new
 * one finalizes the prior active siblings of the same type (they stay in history,
 * read-only). The artifacts reducer enforces this — declare a type here and any
 * artifact of it gets single-active-in-history for free. Keywords: the agent
 * re-runs research and re-surfaces; only the latest table should be live.
 */
export const SupersedableArtifactTypes = ["keywords"] as const;
export function isSupersedableType(type: string): boolean {
  return (SupersedableArtifactTypes as readonly string[]).includes(type);
}

/**
 * Per-row review state. Reuses the canonical Status vocabulary (`core.ts`,
 * shared with Rails) — narrowed to the three a reviewer produces. `satisfies`
 * guarantees we never drift from the existing statuses.
 */
export const ReviewStatuses = ["pending", "accepted", "declined"] as const satisfies readonly Status[];
export type ReviewStatus = (typeof ReviewStatuses)[number];

/**
 * A table's approval (HITL) interaction. Reuses the canonical
 * HumanInterruptConfig (accept / reject / respond / edit) so the InterruptCard
 * buttons render straight off it, plus a table-specific granularity (per-row vs
 * the whole table). Putting `approval: false` on the data makes the table
 * display-only. Row outcome maps: allow_accept → "accepted",
 * allow_ignore (reject) → "declined".
 */
export const tableApprovalSchema = humanInterruptConfigSchema.extend({
  granularity: z.enum(["rows", "all"]).default("rows"),
});
export type TableApproval = z.infer<typeof tableApprovalSchema>;

/** The pre-baked default: per-row approve / reject, no respond or edit. */
export const DEFAULT_TABLE_APPROVAL: TableApproval = {
  granularity: "rows",
  allow_accept: true,
  allow_ignore: true,
  allow_respond: false,
  allow_edit: false,
};

// --- Table data shape (v1's only DisplayArtifact payload) ---
export const tableColumnSchema = z.object({
  key: z.string().describe("Row field this column reads."),
  label: z.string().optional().describe("Human header; defaults to key."),
});
export type TableColumn = z.infer<typeof tableColumnSchema>;

export const tableRowSchema = z
  .object({
    id: z.string().describe("Stable row id (e.g. the keyword)."),
    status: z.enum(ReviewStatuses).default("pending").describe("Reviewer state."),
  })
  .catchall(z.unknown()); // cell values, keyed by column.key
export type TableRow = z.infer<typeof tableRowSchema>;

export const tableArtifactDataSchema = z.object({
  columns: z.array(tableColumnSchema),
  rows: z.array(tableRowSchema),
  // The market the metrics are scoped to ("United States" | "Canada"), echoed
  // from the keyword_research tool so the table can show which region it
  // represents. Optional — non-geo tables omit it.
  location: z.string().optional(),
  // HITL is pre-baked for tables: omit and the agent gets per-row approve/reject
  // (DEFAULT_TABLE_APPROVAL). Pass `false` for a display-only table, or a full
  // HumanInterruptConfig + granularity to customize. Never inferred from type.
  approval: z.union([z.literal(false), tableApprovalSchema]).default(DEFAULT_TABLE_APPROVAL),
});
export type TableArtifactData = z.infer<typeof tableArtifactDataSchema>;

// --- Artifact (broad — what lives in the channel) ---
export const artifactSchema = z.object({
  id: z.string(),
  type: z.enum(ArtifactTypes),
  status: z.enum(Statuses).optional(), // reuse core Statuses; optional for v1
  // Harness lock: set once the harness has actioned the user's response to this
  // artifact (e.g. persisted reviewed keywords). Distinct from `status`
  // ("completed" = user responded); `resolved` = the harness handled that
  // response, so handleArtifactResponse never re-fires the side-effect.
  resolved: z.boolean().optional(),
  updatedAt: z.number(),
  data: z.unknown(), // type-specific; for "table": TableArtifactData
});
export type Artifact = z.infer<typeof artifactSchema>;

/**
 * The artifact's lifecycle `status` answers "has the user responded?" — the one
 * signal that finalizes a reviewable artifact. A freshly surfaced artifact has
 * no status (the user is still reviewing); approving stamps it `completed`, and
 * a completed artifact renders read-only. This is explicit state on the
 * artifact, NOT inferred from row statuses or surrounding chat messages.
 */
export const ARTIFACT_RESPONDED_STATUS = "completed" as const satisfies Status;

export function isArtifactResponded(artifact: Pick<Artifact, "status">): boolean {
  return artifact.status === ARTIFACT_RESPONDED_STATUS;
}

/** Mark an artifact as responded-to (returns a new object; whole-replace by id). */
export function markArtifactResponded<T extends { status?: Status }>(artifact: T): T {
  return { ...artifact, status: ARTIFACT_RESPONDED_STATUS };
}

// --- DisplayArtifact (subset: table | chart) ---
export const displayArtifactSchema = artifactSchema.extend({
  type: z.enum(DisplayArtifactTypes),
});
export type DisplayArtifact = z.infer<typeof displayArtifactSchema>;

// --- CreateOrEditArtifact tool input (single source; tool just wires it) ---
// A `keywords` artifact is surfaced by REFERENCE: the agent names keywords from
// the latest keyword_research result and the system hydrates the real rows +
// builds the table. The agent never authors the data, so numbers can't drift.
// (Data-shaped types — chart/table — would carry their own `data`; not wired.)
export const createOrEditArtifactInputSchema = z.object({
  id: z.string().optional().describe("Omit to create; pass an existing id to edit in place."),
  type: z.literal("keywords").describe("Artifact type."),
  references: z
    .array(z.string())
    .min(1)
    .describe("Keywords to surface — exact strings from the latest keyword_research result."),
});
export type CreateOrEditArtifactInput = z.infer<typeof createOrEditArtifactInputSchema>;
